import { computed } from "vue";
import { useAccountStore } from "@/stores/accountStore";
import { GetAccountInfoUseCase } from "@/application/use-cases/UserUseCases";
import { UserService } from "@/application/services/UserService";
import { ApiUserRepository } from "@/infrastructure/repositories/ApiUserRepository";
import { ToastNotificationService } from "@/infrastructure/services/ToastNotificationService";
import { useLogger } from "./useLogger";
const logger = useLogger();
// Lazy initialization of use case
let getAccountInfoUseCase = null;
// Tracks the in-flight fetch promise so concurrent callers can await it
let pendingFetchPromise = null;
function getUseCase() {
    if (!getAccountInfoUseCase) {
        const userRepository = new ApiUserRepository();
        const notificationService = new ToastNotificationService();
        const userService = new UserService(userRepository, notificationService);
        getAccountInfoUseCase = new GetAccountInfoUseCase(userService);
    }
    return getAccountInfoUseCase;
}
/**
 * Composable for managing account information
 */
export function useAccountInfo() {
    const accountStore = useAccountStore();
    /**
     * Fetch account information from API
     */
    const fetchAccountInfo = async () => {
        // If a fetch is already in progress, await the same promise instead of returning early
        if (pendingFetchPromise) {
            return pendingFetchPromise;
        }
        pendingFetchPromise = (async () => {
            try {
                accountStore.setLoading(true);
                accountStore.clearError();
                logger.debug("Fetching account information...");
                const accountInfoUseCase = getUseCase();
                const info = await accountInfoUseCase.execute();
                // Handle case where API returns success but no account info (result is null)
                if (info) {
                    accountStore.setAccountInfo(info);
                    logger.debug("Account information loaded successfully");
                }
                else {
                    // No account info found - this is not an error, just means no onboarding needed
                    logger.debug("No account information found - onboarding icon will be hidden");
                    accountStore.reset();
                }
            }
            catch (err) {
                // Set error in store to distinguish between "API failed" and "onboarding incomplete"
                const errorMessage = err instanceof Error ? err.message : "Failed to load account information";
                accountStore.setError(errorMessage);
                logger.debug("Failed to fetch account information:", err);
            }
            finally {
                accountStore.setLoading(false);
                pendingFetchPromise = null;
            }
        })();
        return pendingFetchPromise;
    };
    return {
        // State from store
        accountInfo: computed(() => accountStore.accountInfo),
        isLoading: computed(() => accountStore.isLoading),
        error: computed(() => accountStore.error),
        hasAccountInfo: computed(() => accountStore.hasAccountInfo),
        // show icon when onboardingStatus is completed
        showOnboardingIcon: computed(() => accountStore.showOnboardingIcon),
        // Check if account status is "Active"
        isAccountActive: computed(() => accountStore.isAccountActive),
        // Actions
        fetchAccountInfo,
        resetAccountInfo: accountStore.reset,
    };
}
